import type { FC } from 'react';

import { type MockupLogo, logoTextureImageSrc } from '../../types/mockupLogos';
import { MeasurementSystem } from '@seddi/core/lib/cjs';
import { Spinner } from '@seddi/ui-kit';
import { CloseIcon, PlusIcon, ReplaceIcon } from '../../icons/GraphicIcons';
import { GraphicProperties } from '../GraphicProperties/GraphicProperties';
import { useGraphicsPanel } from './GraphicsPanel.hooks';
import * as S from './GraphicsPanel.styles';
import type { StyleLogoRegion } from '../../api/styleAggregator';

type Props = {
  disabled?: boolean;
  units?: MeasurementSystem;
  editableRegions?: StyleLogoRegion[];
  blockBrandLogoRegions?: boolean;
  onAddGraphic?: (file: File, logo: MockupLogo) => void;
  onReplaceGraphic?: (file: File, logoId: string) => void;
};

export const GraphicsPanel: FC<Props> = ({
  disabled = false,
  units = MeasurementSystem.Imperial,
  editableRegions = [],
  blockBrandLogoRegions = false,
  onAddGraphic,
  onReplaceGraphic,
}) => {
  const {
    ACCEPTED_FILE_TYPES,
    blockBrandLogoRegions: shouldBlockBrandLogoRegions,
    conversionReplacingId,
    getRootProps,
    getInputProps,
    handleAddClick,
    handleDeleteClick,
    handleDeselect,
    handleReplaceClick,
    handleReplaceFileChange,
    handleSelectLogo,
    isConversionLoading,
    isDragActive,
    logos,
    regionOptions,
    replaceInputRef,
    editableRegions: availableRegions,
    styleRegions,
    selectedId,
    selectedLogo,
  } = useGraphicsPanel({
    disabled,
    units,
    editableRegions,
    blockBrandLogoRegions,
    onAddGraphic,
    onReplaceGraphic,
  });

  const handleThumbnailClick = (logoId: string) => {
    if (logoId === selectedId) {
      handleDeselect();
    } else {
      handleSelectLogo(logoId);
    }
  };

  return (
    <S.PanelContainer {...getRootProps()} $disabled={disabled} $isDragActive={isDragActive}>
      <input {...getInputProps({ style: { display: 'none' } })} />
      {isDragActive && <S.DropzoneOverlay>Drop your graphic file here...</S.DropzoneOverlay>}
      <S.PanelBody $dimmed={isDragActive}>
        <S.GraphicsContainer
          $itemCount={logos.length + (isConversionLoading && !conversionReplacingId ? 1 : 0)}
        >
          <S.AddGraphicButton data-add-button onClick={handleAddClick}>
            <PlusIcon />
          </S.AddGraphicButton>
          {logos.map(logo => {
            const thumbSrc = logoTextureImageSrc(logo);
            return (
              <S.GraphicThumbnail
                key={logo.id}
                data-graphic-thumbnail
                $selected={logo.id === selectedId}
                onClick={() => handleThumbnailClick(logo.id)}
                title={logo.displayName || logo.name}
              >
                {thumbSrc && <img src={thumbSrc} alt={logo.displayName || logo.name} />}
                {conversionReplacingId === logo.id && (
                  <S.ThumbnailLoadingOverlay>
                    <Spinner type="orbit" />
                  </S.ThumbnailLoadingOverlay>
                )}
                <S.ReplaceButton
                  onClick={e => handleReplaceClick(e, logo.id)}
                  title="Replace graphic"
                >
                  <ReplaceIcon />
                </S.ReplaceButton>
                <S.DeleteButton onClick={e => handleDeleteClick(e, logo)} title="Delete graphic">
                  <CloseIcon />
                </S.DeleteButton>
              </S.GraphicThumbnail>
            );
          })}
          {isConversionLoading && !conversionReplacingId && (
            <S.LoadingThumbnail>
              <Spinner type="orbit" />
            </S.LoadingThumbnail>
          )}
        </S.GraphicsContainer>
        {selectedLogo && (
          <S.PropertiesContainer>
            <GraphicProperties
              logo={selectedLogo}
              regionOptions={regionOptions}
              editableRegions={availableRegions}
              blockBrandLogoRegions={shouldBlockBrandLogoRegions}
              styleRegions={styleRegions}
              units={units}
            />
          </S.PropertiesContainer>
        )}
      </S.PanelBody>

      <input
        ref={replaceInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleReplaceFileChange}
      />
    </S.PanelContainer>
  );
};
